import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FC, MouseEvent } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import { FormLayout, Input, Label, Larger } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import Note from '@/components/Forms/Note';
import { Modal } from '@/components/Modal';
import Text from '@/components/Text';
import useNetwork from '@/hooks/dq/useNetwork';
import useAlerts from '@/hooks/useAlerts';
import { EditorialQueuePage } from '@/types/resources';
import { api } from '@/utils/api';
import { EditorialFacebookPostType } from '@/utils/enums';
import { handleFormErrors } from '@/utils/helpers';
import { RootState } from '@/utils/store';

const Heading = styled(Text)`
  margin: 0 0 24px;
  text-align: center;
`;

const Dialog = styled(StyledDialog)`
  width: 464px;
  padding: 56px 24px 24px;
  text-align: left;
`;

const Footer = styled.div`
  padding: 24px 32px 32px;
  box-shadow: 0px -1px 8px 0px #2222220f;
  display: flex;
  margin: 56px -24px -24px;
  gap: 24px;
  > button {
    flex: 1;
  }
`;

const StyledNote = styled(Note)`
  background: ${({ theme }) => theme.colors.grayscaleLight};
`;

const NetworkInput = styled(Input)`
  text-transform: capitalize;
`;

interface FormDataT {
  channel_id: number;
  post_type: EditorialFacebookPostType;
  option: string;
  owner: string;
}

interface Props {
  onClose: VoidFunction;
  activePage: EditorialQueuePage;
}

const PlaceholderPostForm: FC<Props> = ({ onClose, activePage }) => {
  const alerts = useAlerts();

  const [network, networkLabel] = useNetwork();

  const queryClient = useQueryClient();

  const { data: channels } = useQuery(
    ['editorial_distribution_queue_channels'],
    () => api.get<never, EditorialQueuePage[]>('/v1/editorial/queue/channels'),
    {
      staleTime: 120_000,
      select: (data) =>
        data.map((channel) => ({ label: channel.name, value: channel.id })),
    },
  );

  const userName = useSelector((state: RootState) => state.auth.user!.name);

  const {
    handleSubmit,
    clearErrors,
    control,
    setError,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      post_type: EditorialFacebookPostType.PLACEHOLDER,
      channel_id: activePage.id,
      option: 'prepend',
      owner: userName,
    },
  });

  const onSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    clearErrors();
    return handleSubmit(async (data) => {
      try {
        await api.post(`/v1/editorial/queue/prepend`, {
          ...data,
          network,
        });
        await queryClient.invalidateQueries([
          'editorial_distribution_schedules',
        ]);

        onClose();

        alerts.success('A new time gap has been added successfully');
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true);
      }
    })(e);
  };

  return (
    <Modal zIndex={50}>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <Heading $type="heading2">Time Gap Placeholder</Heading>
        <FormLayout>
          <FormField<FormDataT, number>
            label="Page Name"
            type="select"
            control={control}
            name="channel_id"
            options={channels || []}
          />
          <Larger>
            <Label>Network</Label>
            <NetworkInput readOnly disabled value={networkLabel} />
          </Larger>
          <Larger>
            <StyledNote>
              Please note, the time gap will be created first in the list. Turn
              on reorder mode and drag the time gap to your needed slot.
            </StyledNote>
          </Larger>
        </FormLayout>
        <Footer>
          <Button
            onClick={onSubmit}
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            Create
          </Button>
          <Button color="secondary" onClick={onClose}>
            Cancel
          </Button>
        </Footer>
        <CloseButton onClick={onClose}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 13L1 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseButton>
      </Dialog>
    </Modal>
  );
};

export default PlaceholderPostForm;
